import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GatewayService, type UpdateClientPayload } from "../api/gateway.service";
import type { Client } from "@shared-types";

/** State and action returned by {@link useUpdateClient}. */
export interface UseUpdateClientReturn {
    /** Update a client by id; resolves with the updated record. */
    updateClient: (id: number, data: UpdateClientPayload) => Promise<Client>;
    loading: boolean;
    error: Error | null;
    data: Client | null;
}

/** Update a client and invalidate the affected cache entries on success. */
export const useUpdateClient = (): UseUpdateClientReturn => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateClientPayload }) =>
            GatewayService.updateClient(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["gateway", "clients"] });
            queryClient.invalidateQueries({ queryKey: ["gateway", "client", id] });
        },
    });
    return {
        updateClient: (id, data) => mutation.mutateAsync({ id, data }),
        loading: mutation.isPending,
        error: mutation.error,
        data: mutation.data ?? null,
    };
};
